import { Graph, Node, Path, Cell, CellView } from '@antv/x6'
import '@antv/x6-vue-shape'
import { getGraphData, invisiblePortAttrs, portAttrs } from './get-graph-data'
import store from '@ps/store'
import { EdgeView } from '@antv/x6/lib/view/edge'
import {
  cookDagNodePosition,
  cookSceneNodeLayoutData
} from './layout'
import {
  StoryBoardLayout
} from '@ps/store/workbench/storyboard/index.type'
import config from './config'
import { isScene } from './utils'
import nodePool from '@ps/bridge/qc/node-pool'
import generateId from '@com/utils/generate-id'
import { addEdge, edgeAttrs } from '@ps/store/workbench/storyboard/edge'
import _ from 'lodash'
import { addScene } from '@ps/store/workbench/storyboard/scene-node'
import { addDagNode } from '@ps/store/workbench/storyboard/dag-node'
import { layout } from './layout'
import { getSceneNodeByUuid } from '@ps/bridge/qc/base'
import { getSwitchSceneEventData } from '@ps/bridge/qc/vp'
import { SwitchSceneEventData } from '@ps/bridge/qc/scripts/editor'

export function getContainerSize() {
  const container = document.getElementById('story-board') as HTMLElement
  return {
    width: container.clientWidth,
    height: container.clientHeight
  }
}

function bindEvents({
  graph,
  data
}: {
  graph: Graph;
  data: {
    nodes: Node.Metadata[];
    scenes: Node.Metadata[];
    edges: Node.Metadata[];
  };
}) {
  graph.on('scale', ({ sx, sy, ox, oy }) => {
    store.dispatch('workspace/game/device/updateScale', sx)
    const currentSceneUuid = store.state.workspace.storyboard.currentSceneUuid
    // 缩放时，同步故事板当前场景节点与 iframe 的位置
    if (currentSceneUuid) {
      store.commit(
        'workspace/storyboard/moveSceneEditorToTargetScenePosition',
        {
          sceneId: currentSceneUuid
        }
      )
    }
  })

  graph.on('translate', ({ tx, ty }) => {
    store.commit('workspace/storyboard/updateGraphLayoutData', { tx, ty })
    const currentSceneUuid = store.state.workspace.storyboard.currentSceneUuid
    // 缩放时，同步故事板当前场景节点与 iframe 的位置
    if (currentSceneUuid) {
      store.commit(
        'workspace/storyboard/moveSceneEditorToTargetScenePosition',
        {
          sceneId: currentSceneUuid
        }
      )
    }
  })

  // graph.on('resize', ({ width, height }) => { })
  // graph.on('cell:click', ({ e, x, y, cell, view }) => {
  //   console.log('cell:click', e)
  // })

  // graph.on('node:click', async({ node, e }) => {
  //   console.log('node:click')
  // })

  graph.on('edge:selected', async({ edge }) => {
    console.log('edge:selected')
    // 连线后切换场景和节点（这样事件列表才展示出正确的信息）
    const source = edge.getSourceCell()
    if (source) {
      const sourceData = source.data
      store.commit('workspace/game/visualProgram/updateCurrentEventData', {
        actionIds: edge.data.actionIds,
        eventIds: edge.data.eventIds
      })
      await store.dispatch('workspace/storyboard/selectNode', {
        sceneId: sourceData.sceneId,
        id: sourceData.nodeId
      })
    }
  })

  graph.on('node:moving', ({ e, node, x, y }) => {
    // 移动故事板的场景节点时要把场景里的子节点也移位置
    // 如果被移动的故事板的场景节点不是当前激活场景，则获取的 x、y 不正确，所以这里的逻辑的前提条件就是一定得保证playable当前激活的场景是正确的
    const refChildNodes =
      data.nodes.filter(
        item =>
          item.data.sceneId === node.data.sceneId && !item.data.isSceneNode
      ) || []
    refChildNodes.forEach(item => {
      const targetNode = graph.getCellById(item.data.nodeId) as Node
      const position = cookDagNodePosition({
        id: item.data.nodeId,
        sceneNode: node,
        useCache: true
      })
      if (position) {
        const { x, y } = position
        targetNode.position(x, y)
      }
    })
    const position = node.position()
    store.commit('workspace/storyboard/updateTargetNodeLayoutData', {
      id: node.id,
      x: position.x,
      y: position.y
    })
  })

  function hideOutPort(node: Node) {
    node.prop('ports/groups/out/attrs', invisiblePortAttrs)
  }
  function showOutPort(node: Node) {
    node.prop('ports/groups/out/attrs', portAttrs)
  }
  graph.on('node:mouseenter', ({ node }) => {
    store.commit('workspace/storyboard/updateHoverX6Node', node)
    if (!node.data.isSceneNode && !node.data.switchSceneId) {
      // 动态新增的 port，位置和样式都无法自定义，所以使用修改样式的方式去处理 port
      showOutPort(node)
    }
  })
  graph.on('node:mouseleave', ({ node }) => {
    store.commit('workspace/storyboard/updateHoverX6Node', null)
    if (!node.data.isSceneNode && !node.data.switchSceneId) {
      hideOutPort(node)
    }
  })

  graph.on('node:selected', async({ node }) => {
    console.log('node:selected')
    const currentX6Node = store.state.workspace.storyboard.currentX6Node
    const isEditScene = store.state.workspace.storyboard.isEditScene
    const currentSceneUuid = store.state.workspace.storyboard.currentSceneUuid
    // 如果 id 与当前节点的 id 不一样，说明切换了 x6 Node，需要清空 visualProgram.currentEventData
    if (currentX6Node && node.id !== currentX6Node.id) {
      store.commit('workspace/game/visualProgram/updateCurrentEventData', {
        actionIds: null,
        eventIds: null
      })
      const scene = getSceneNodeByUuid(node.id)
      if (scene && scene.uuid !== currentSceneUuid && isEditScene) {
        await store.dispatch('workspace/storyboard/exitEditScene', {
          sceneId: node.data.sceneId
        })
        await store.dispatch('workspace/storyboard/renderDagNodes')
      }
    }
    await store.dispatch('workspace/storyboard/selectNode', {
      sceneId: node.data.sceneId,
      id: node.id
    })
    // 主动触发一次渲染边，因为但是 watch 当前场景节点或当前节点变化并不能保证边能够重新渲染（如选择 a 场景，然后点击空白区域会渲染所有节点，但是再点击 a 场景并不会渲染 a 场景对应的节点）
    await store.dispatch('workspace/storyboard/renderEdges', {
      sceneId: node.data.sceneId,
      id: node.id
    })
  })

  // 删除链接
  graph.on(
    'edge:delete',
    async({ edge }: { edge: EdgeView & { data: SwitchSceneEventData } }) => {
      const switchSceneEventData = getSwitchSceneEventData()
      const targetData = edge.data
      const deleteActions = switchSceneEventData.filter(switchScene => {
        return (
          switchScene.switchSceneId === targetData.switchSceneId &&
          switchScene.nodeId === targetData.nodeId
        )
      })
      store.dispatch('workspace/game/visualProgram/batchDelAction', {
        deleteActions,
        sceneId: targetData.sceneId || targetData.nodeId
      })
    }
  )

  graph.on('blank:click', async({ e, x, y }) => {
    console.log('blank:click', e)
    const currentSceneUuid = store.state.workspace.storyboard.currentSceneUuid
    await store.dispatch('workspace/storyboard/exitEditScene', { sceneId: currentSceneUuid })
    await store.dispatch('workspace/storyboard/renderDagNodes')
    await store.dispatch('workspace/storyboard/renderEdges')
  })

  graph.on('edge:mouseenter', ({ edge }) => {
    edge.addTools([
      // 增加终点拖拽节点工具
      'target-arrowhead'
    ])
  })

  graph.on('edge:mouseleave', ({ edge }) => {
    edge.removeTools()
  })

  graph.on('edge:connected', async({ isNew, edge, previousCell, currentCell }) => {
    // 新增
    if (isNew) {
      await store.dispatch('workspace/storyboard/addSwitchSceneEventToTargetScene', {
        isNew,
        edge,
        previousCell,
        currentCell
      })
    } else {
      if (!previousCell || !currentCell) return
      const edgeData = _.cloneDeep(edge.data)
      const switchSceneEventData = getSwitchSceneEventData()
      const actionData = switchSceneEventData.filter(switchScene => {
        return (
          switchScene.switchSceneId === edge.data.switchSceneId &&
          switchScene.nodeId === edge.data.nodeId
        )
      })
      const node = nodePool.find(edge.data.nodeId)
      await store.dispatch('workspace/game/visualProgram/changeEventToOtherScene', {
        actionData,
        node,
        sceneId: edge.data.sceneId || edge.data.nodeId,
        targetSceneId: currentCell.id
      })
      if (edgeData) {
        await store.dispatch('workspace/storyboard/selectNode', {
          sceneId: edgeData.sceneId,
          id: edgeData.nodeId
        })
      }
    }
  })
}

export function init(): { graph: Graph } {
  Graph.registerEdge(
    'dag-edge',
    {
      inherit: 'edge',
      attrs: edgeAttrs
    },
    true
  )

  Graph.registerConnector(
    'algo-connector',
    (s, e) => {
      const offset = 4
      const deltaY = Math.abs(e.y - s.y)
      const control = Math.floor((deltaY / 3) * 2)

      const v1 = { x: s.x, y: s.y + offset + control }
      const v2 = { x: e.x, y: e.y - offset - control }

      return Path.normalize(
        `M ${s.x} ${s.y}
       L ${s.x} ${s.y + offset}
       C ${v1.x} ${v1.y} ${v2.x} ${v2.y} ${e.x} ${e.y - offset}
       L ${e.x} ${e.y}
      `
      )
    },
    true
  )

  const { width, height } = getContainerSize()
  const graph: Graph = new Graph({
    container: document.getElementById('story-flow')!,
    width,
    height,
    panning: {
      enabled: true,
      eventTypes: ['leftMouseDown', 'mouseWheel']
    },
    mousewheel: {
      enabled: true,
      modifiers: 'ctrl',
      factor: 1.1,
      maxScale: 2,
      minScale: 0.5
    },
    highlighting: {
      magnetAdsorbed: {
        name: 'stroke',
        args: {
          attrs: {
            fill: '#fff',
            stroke: '#31d0c6',
            strokeWidth: 4
          }
        }
      }
    },
    connecting: {
      snap: true,
      allowBlank: false,
      allowLoop: false,
      highlight: true,
      connector: 'algo-connector',
      connectionPoint: 'anchor',
      anchor: 'center',
      validateMagnet({ magnet }) {
        // out 为新增边
        return magnet.getAttribute('port-group') === 'out'
      },
      createEdge() {
        return graph.createEdge({
          id: generateId(),
          shape: 'dag-edge',
          attrs: {
            line: {
              strokeDasharray: '5 5'
            }
          },
          data: {
            disableMove: true,
            shape: config.nodeTypes.edge
          },
          zIndex: config.edgeZIndex
        })
      },
      validateConnection({ sourceView, targetCell, targetView, targetMagnet }) {
        if (!targetMagnet) {
          return false
        }

        // 不是场景节点，不可以连接
        if (!targetCell?.data.isSceneNode) {
          return false
        }

        // 如果目标不是输入端口，不可以连接
        if (targetMagnet.getAttribute('port-group') !== 'in') {
          return false
        }

        return true
      }
    },
    selecting: {
      enabled: true,
      multiple: false,
      rubberEdge: true,
      rubberNode: true,
      modifiers: 'shift',
      rubberband: true,
      movable: false
    },
    interacting: function(cellView: CellView) {
      return {
        // 只有场景节点才可以移动
        // 编辑模式不可移动
        nodeMovable:
          cellView.cell.getData().nodeMovable &&
          !store.state.workspace.storyboard.isEditScene,
        edgeMovable: true,
        edgeLabelMovable: false,
        arrowheadMovable: true,
        vertexMovable: true,
        vertexAddable: true,
        vertexDeletable: true,
        useEdgeTools: true,

        // node
        magnetConnectable: true,
        stopDelegateOnDragging: true
      }
    },
    keyboard: {
      enabled: true
    }
  })

  graph.on('edge:connected', ({ edge }) => {
    edge.attr({
      line: {
        strokeDasharray: ''
      }
    })
  })

  graph.on('node:change:data', ({ node }) => {
    // const edges = graph.getIncomingEdges(node)
    // const { status } = node.getData() as NodeStatus
    // edges?.forEach(edge => {
    //   if (status === 'running') {
    //     edge.attr('line/strokeDasharray', 5)
    //     edge.attr('line/style/animation', 'running-line 30s infinite linear')
    //   } else {
    //     edge.attr('line/strokeDasharray', '')
    //     edge.attr('line/style/animation', '')
    //   }
    // })
  })

  // 初始化节点/边
  const initCells = ({
    graphData
  }: {
    graphData: {
      nodes: Cell.Metadata[] | undefined;
      edges: Cell.Metadata[] | undefined;
      scenes: Cell.Metadata[] | undefined;
    };
  }) => {
    const cells: Cell[] = []
    const nodeUtils = {
      sceneNode({ metaData }: { metaData: Cell.Metadata }) {
        const node = addScene({
          graph,
          metaData
        })
        cells.push(node)
      },
      node({ metaData }: { metaData: Cell.Metadata }) {
        const node = addDagNode({
          metaData,
          graph,
          x: 0,
          y: 0
        })
        cells.push(node)
      },
      edge({ metaData }: { metaData: Cell.Metadata }) {
        const edge = addEdge({
          graph,
          metaData
        })
        cells.push(edge)
      }
    }

    // 按照 scene -> node -> edge 的顺序，保证渲染的时候不会查找不到某个节点，如渲染 edge 的时候，node 或 scene 是必须先渲染的
    graphData.scenes?.forEach(metaData => {
      nodeUtils.sceneNode({ metaData })
    })
    graphData.nodes?.forEach(metaData => {
      nodeUtils.node({ metaData })
    })
    graphData.edges?.forEach(metaData => {
      nodeUtils.edge({ metaData })
    })

    graph.resetCells(cells)
  }

  const data = getGraphData()
  bindEvents({ graph, data })

  const storyboardLayout = store.state.workspace.storyboard.layout
  const adapterResolution = store.state.workspace.game.device.adapterResolution
  // 如果 layout 数据不为空，说明已经初始化过故事板，所以用 layout 数据即可
  // 新增场景和删除场景时，即使不是在故事板视图，都需要更新 layout 数据，保证 layout 数据正常
  if (!_.isEmpty(storyboardLayout[adapterResolution])) {
    data.scenes.forEach(scene => {
      // 如果有 layout 数据，则使用 layout 数据
      let attrs = storyboardLayout[adapterResolution][scene.id as string]
      if (!attrs) {
        console.log(
          `找到了${adapterResolution}的布局数据，但是没有${scene.id}的布局数据，默认 x: 0, y: 0`
        )
        attrs = {
          x: 0,
          y: 0
        }
      }
      if (!attrs.x) {
        console.log(`找到了${scene.id}的布局数据，但是没有 x 值，默认使用 0`)
      }
      if (!attrs.y) {
        console.log(`找到了${scene.id}的布局数据，但是没有 y 值，默认使用 0`)
      }
      scene.x = attrs.x || 0
      scene.y = attrs.y || 0
    })
  } else {
    const container = document.querySelector(
      '#scene-editor-container'
    ) as HTMLElement
    const width = container.clientWidth
    cookSceneNodeLayoutData({
      nodes: data.nodes,
      scenes: data.scenes,
      edges: data.edges,
      width
    })
  }

  initCells({
    graphData: data
  })
  // dag-node 初始化时 x、y 都是 0，所以需要做一次布局
  layout({
    graph
  })

  return {
    graph
  }
}

export function getLayoutFromGraph({
  graph,
  adapterResolution
}: {
  graph: Graph;
  adapterResolution: 'pdef' | 'ldef';
}): StoryBoardLayout {
  const layoutData: StoryBoardLayout = {}
  const data = graph
    .getCells()
    // 只需要场景节点数据
    .filter(item => isScene(item.data.shape))
    .map(item => ({
      ...(item as Node).getPosition(),
      id: item.data.sceneId
    }))
  console.log(data)
  data.forEach(item => {
    layoutData[item.id] = layoutData[item.id] ? layoutData[item.id] : {}
    layoutData[item.id][adapterResolution] = item
  })
  return layoutData
}
